import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ClipboardListIcon, PlusIcon, StickyNoteIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CurrentVehicleNotice, CurrentVehicleSelect } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Segmented, Skeleton } from "@/components/ui/misc";
import { ApplyTemplateDialog } from "@/features/templates/apply-template-dialog";
import { WorkOrderRow } from "@/features/work-orders/work-order-row";
import { WorkOrderSheet } from "@/features/work-orders/work-order-sheet";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { searchEnum, searchText } from "@/lib/search";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";

const SCOPES = ["open", "completed", "all"] as const;

export const Route = createFileRoute("/work-orders/")({
  validateSearch: z.object({
    scope: searchEnum(SCOPES),
    new: searchText,
  }),
  component: WorkOrdersPage,
});

type WorkOrder = RouterOutputs["workOrders"]["list"][number];

function WorkOrdersPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scope = search.scope ?? "open";
  // The current vehicle is the work order list's vehicle filter (ADR 0008).
  const { vehicleId } = useCurrentVehicle();
  const [applying, setApplying] = useState(false);
  const { data: workOrders, isLoading } = useQuery({
    ...trpc.workOrders.list.queryOptions({ scope, vehicleId }),
    placeholderData: (p) => p,
  });

  const groups: { key: string; title: string; items: WorkOrder[] }[] = workOrders
    ? [
        {
          key: "in_progress",
          title: t("workOrders.groupInProgress"),
          items: workOrders.filter((w) => w.status === "in_progress"),
        },
        {
          key: "planned",
          title: t("workOrders.groupPlanned"),
          items: workOrders.filter((w) => w.status === "planned"),
        },
        {
          key: "idea",
          title: t("workOrders.groupIdeas"),
          items: workOrders.filter((w) => w.status === "idea"),
        },
        {
          key: "completed",
          title: t("workOrders.groupCompleted"),
          items: workOrders.filter((w) => w.status === "completed"),
        },
        {
          key: "cancelled",
          title: t("workOrders.groupCancelled"),
          items: workOrders.filter((w) => w.status === "cancelled"),
        },
      ].filter((g) => g.items.length > 0)
    : [];

  return (
    <Page>
      <PageHeader
        title={t("workOrders.title")}
        actions={
          <>
            <Button variant="outline" onClick={() => setApplying(true)}>
              <StickyNoteIcon />
              {t("workOrders.fromTemplate")}
            </Button>
            <Button onClick={() => void navigate({ search: (s) => ({ ...s, new: "1" }) })}>
              <PlusIcon />
              {t("workOrders.new")}
            </Button>
          </>
        }
      />
      <CurrentVehicleNotice />
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Segmented
          value={scope}
          onChange={(value) =>
            void navigate({
              search: (s) => ({ ...s, scope: value === "open" ? undefined : value }),
              replace: true,
            })
          }
          options={[
            { value: "open", label: t("workOrders.tabOpen") },
            { value: "completed", label: t("workOrders.tabCompleted") },
            { value: "all", label: t("workOrders.tabAll") },
          ]}
        />
        <CurrentVehicleSelect className="sm:w-64" />
      </div>

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={ClipboardListIcon}
          title={
            scope === "completed" ? t("workOrders.emptyCompleted") : t("workOrders.emptyTitle")
          }
          body={scope === "completed" ? undefined : t("workOrders.emptyBody")}
          action={
            scope === "completed" ? undefined : (
              <Button onClick={() => void navigate({ search: (s) => ({ ...s, new: "1" }) })}>
                <PlusIcon />
                {t("workOrders.new")}
              </Button>
            )
          }
        />
      ) : (
        <div className="flex flex-col gap-7">
          {groups.map((group) => (
            <section key={group.key}>
              <SectionTitle>
                {group.title}
                <span className="ml-2 text-sm font-normal text-muted-foreground tabular">
                  {group.items.length}
                </span>
              </SectionTitle>
              <Card className="divide-y divide-border">
                {group.items.map((wo) => (
                  <WorkOrderRow key={wo.id} wo={wo} />
                ))}
              </Card>
            </section>
          ))}
        </div>
      )}

      <WorkOrderSheet
        open={search.new === "1"}
        onOpenChange={(open) =>
          void navigate({ search: (s) => ({ ...s, new: open ? "1" : undefined }) })
        }
        vehicleId={vehicleId ?? undefined}
        onSaved={(id) =>
          void navigate({
            to: "/work-orders/$workOrderId",
            params: { workOrderId: id },
            search: {},
          })
        }
      />
      <ApplyTemplateDialog
        open={applying}
        onOpenChange={setApplying}
        vehicleId={vehicleId ?? undefined}
      />
    </Page>
  );
}
